import {
  CalendarOutlined,
  DollarOutlined,
  DownOutlined,
  EyeOutlined,
  FileTextOutlined,
  GlobalOutlined,
  PlusOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TagOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Dropdown,
  MenuProps,
  Result,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ModalAddAwb from "../../components/modal/ModalAddAwb";
import ModalAwbDetail from "../../components/modal/ModalAwbDetail";
import ModalEditPurchaseOrder from "../../components/modal/ModalEditPurchaseOrder";
import { IPurchaseOrderItem } from "../../models/Purchase";
import {
  useGetPurchaseAwbByPurchaseOrderId,
  useGetPurchaseOrderByPoNumber,
  useSendPurchaseOrder,
} from "../../query/PurchaseQuery";
import { notify } from "../../utils/toast";

const { Text, Title } = Typography;

const PurchaseOrderDetail = () => {
  const { alias } = useParams<{ alias: string }>();
  const { data: order, isLoading } = useGetPurchaseOrderByPoNumber(alias);
  const { data: awbData } = useGetPurchaseAwbByPurchaseOrderId(order?.id);
  const sendPurchaseOrder = useSendPurchaseOrder();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddAwbModalOpen, setIsAddAwbModalOpen] = useState(false);
  const [isAwbDetailModalOpen, setIsAwbDetailModalOpen] = useState(false);

  const getStatusTag = (
    status: "DRAFT" | "SENT" | "PARTIALLY_RECEIVED" | "COMPLETED" | "CANCELED",
  ) => {
    const statusConfig = {
      DRAFT: { color: "default", text: "Nháp" },
      SENT: { color: "blue", text: "Đã gửi" },
      PARTIALLY_RECEIVED: { color: "orange", text: "Đã nhận một phần" },
      COMPLETED: { color: "green", text: "Hoàn thành" },
      CANCELED: { color: "red", text: "Đã hủy" },
    };
    const config = statusConfig[status];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleSendPurchaseOrder = async () => {
    if (!order) return;
    try {
      await sendPurchaseOrder.mutateAsync(order.id);
      notify.success("Đơn hàng đã được chốt");
    } catch (error) {
      notify.error("Có lỗi xảy ra khi chốt đơn hàng");
    }
  };

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "edit":
        setIsEditModalOpen(true);
        break;
      case "send":
        handleSendPurchaseOrder();
        break;
      case "cancel":
        break;
      case "print":
        break;
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "edit",
      label: "Chỉnh sửa",
      disabled: order?.status !== "DRAFT",
    },
    {
      key: "send",
      label: "Gửi đơn hàng",
      disabled: order?.status !== "DRAFT",
    },
    {
      key: "cancel",
      label: "Hủy đơn",
      danger: true,
      disabled: order?.status === "COMPLETED" || order?.status === "CANCELED",
    },
    {
      key: "print",
      label: "In đơn hàng",
    },
  ];

  const columns: TableColumnType<IPurchaseOrderItem>[] = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      key: "unit",
      width: 80,
      align: "center",
    },
    {
      title: "Số lượng",
      dataIndex: "quantityBuying",
      key: "quantityBuying",
      width: 100,
      align: "center",
      render: (qty: number) => (
        <Text strong style={{ color: "#1890ff" }}>
          {qty}
        </Text>
      ),
    },
    {
      title: "Đơn giá (VNĐ)",
      dataIndex: "unitPriceExwVnd",
      key: "unitPriceExwVnd",
      width: 140,
      align: "right",
      render: (price: number) => (
        <Text>{Number(price).toLocaleString()} ₫</Text>
      ),
    },
    {
      title: "Đơn giá (USD)",
      dataIndex: "unitPriceExwUsd",
      key: "unitPriceExwUsd",
      width: 120,
      align: "right",
      render: (price: number) => <Text>${Number(price).toFixed(2)}</Text>,
    },
    {
      title: "Thành tiền (VNĐ)",
      dataIndex: "totalExwVnd",
      key: "totalExwVnd",
      width: 150,
      align: "right",
      render: (total: number) => (
        <Text strong style={{ color: "#fa8c16" }}>
          {Number(total).toLocaleString()} ₫
        </Text>
      ),
    },
    {
      title: "Thành tiền (USD)",
      dataIndex: "totalExwUsd",
      key: "totalExwUsd",
      width: 130,
      align: "right",
      render: (total: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          ${Number(total).toFixed(2)}
        </Text>
      ),
    },
    {
      title: "Thuế nhập khẩu",
      dataIndex: "totalImportTax",
      key: "totalImportTax",
      width: 140,
      align: "right",
      render: (tax: number) => (
        <Text type="secondary">{Number(tax).toLocaleString()} ₫</Text>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <Result
        status="404"
        title="404"
        subTitle="Không tìm thấy đơn đặt hàng"
        extra={
          <Button type="primary" onClick={() => navigate("/purchase/orders")}>
            Quay lại danh sách
          </Button>
        }
      />
    );
  }
  const totalItemsAmount = order.items.reduce(
    (sum, item) => sum + Number(item.totalExwVnd),
    0,
  );
  const totalTax = order.items.reduce(
    (sum, item) => sum + Number(item.totalImportTax),
    0,
  );
  const grandTotal = totalItemsAmount + totalTax + Number(order.shippingCharge);

  return (
    <div className="p-4">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Title
              level={3}
              style={{
                margin: 0,
                fontFamily: "Chakra Petch",
                textTransform: "uppercase",
              }}
              className="dark:text-white"
            >
              <ShoppingCartOutlined className="mr-2" />
              {order.pONumber}
            </Title>
            <Space wrap className="mt-2">
              <Tag icon={<ShopOutlined />} color="purple">
                {order.vendorName}
              </Tag>
              {getStatusTag(order.status)}
            </Space>
          </div>
          <div className="flex gap-2">
            <Button
              icon={awbData ? <EyeOutlined /> : <PlusOutlined />}
              disabled={order.status === "DRAFT"}
              onClick={() => {
                awbData
                  ? setIsAwbDetailModalOpen(true)
                  : setIsAddAwbModalOpen(true);
              }}
            >
              {awbData
                ? `AWB: ${awbData.awbCode} · ETA ${dayjs(awbData.eta).format("DD/MM")}`
                : `Thêm AWB`}
            </Button>
            <Button onClick={() => navigate("/purchase/orders")}>
              Quay lại
            </Button>
            <Dropdown
              menu={{ items: menuItems, onClick: handleMenuClick }}
              trigger={["click"]}
            >
              <Button type="primary" icon={<DownOutlined />}>
                Thao tác
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card
          size="small"
          className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800"
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarOutlined className="text-blue-600 text-xl" />
            <Text strong>Tổng tiền (VNĐ)</Text>
          </div>
          <Text strong className="text-2xl block" style={{ color: "#1890ff" }}>
            {Number(order.totalAmount).toLocaleString()} ₫
          </Text>
        </Card>

        <Card
          size="small"
          className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800"
        >
          <div className="flex items-center gap-2 mb-2">
            <GlobalOutlined className="text-green-600 text-xl" />
            <Text strong>Tổng tiền (USD)</Text>
          </div>
          <Text strong className="text-2xl block" style={{ color: "#52c41a" }}>
            ${Number(order.totalAmountUsd).toFixed(2)}
          </Text>
        </Card>

        <Card
          size="small"
          className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800"
        >
          <div className="flex items-center gap-2 mb-2">
            <TagOutlined className="text-orange-600 text-xl" />
            <Text strong>Thuế nhập khẩu</Text>
          </div>
          <Text strong className="text-2xl block" style={{ color: "#fa8c16" }}>
            {order.importTaxPercent}%
          </Text>
          <Text type="secondary" className="text-sm">
            {Number(totalTax).toLocaleString()} ₫
          </Text>
        </Card>

        <Card
          size="small"
          className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800"
        >
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCartOutlined className="text-purple-600 text-xl" />
            <Text strong>Số món hàng</Text>
          </div>
          <Text strong className="text-2xl block" style={{ color: "#722ed1" }}>
            {order.items.length}
          </Text>
        </Card>
      </div>

      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Thông tin đơn hàng</span>
          </Space>
        }
        size="small"
        className="mb-4"
      >
        <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 3 }}>
          <Descriptions.Item label="Mã đơn hàng" span={1}>
            <Text strong copyable>
              {order.pONumber}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Nhà cung cấp" span={1}>
            <Text strong style={{ color: "#722ed1" }}>
              {order.vendorName}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái" span={1}>
            {getStatusTag(order.status)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <CalendarOutlined />
                Ngày đặt hàng
              </Space>
            }
            span={1}
          >
            <Text strong style={{ color: "#1890ff" }}>
              {dayjs(order.orderDate).format("DD/MM/YYYY")}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <CalendarOutlined />
                Ngày giao dự kiến
              </Space>
            }
            span={1}
          >
            <Text strong style={{ color: "#fa8c16" }}>
              {dayjs(order.expectedDeliveryDate).format("DD/MM/YYYY")}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Tỷ giá" span={1}>
            <Text strong>
              1 USD = {Number(order.exchangeRate).toLocaleString()} VNĐ
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Phí vận chuyển" span={1}>
            <Text strong style={{ color: "#fa8c16" }}>
              {Number(order.shippingCharge).toLocaleString()} ₫
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Thuế nhập khẩu" span={1}>
            <Text strong>
              {order.importTaxPercent}% ({Number(totalTax).toLocaleString()} ₫)
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>Danh sách vật tư</span>
          </Space>
        }
        size="small"
        className="mb-4"
        extra={
          <Tag color="blue" style={{ fontSize: "14px" }}>
            {order.items.length} món
          </Tag>
        }
      >
        <Table
          dataSource={order.items}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1400 }}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={7} align="right">
                  <Text strong>Tổng tiền hàng:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ color: "#fa8c16", fontSize: "16px" }}>
                    {Number(totalItemsAmount).toLocaleString()} ₫
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong style={{ color: "#52c41a", fontSize: "16px" }}>
                    ${Number(order.totalAmountUsd).toFixed(2)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong style={{ fontSize: "16px" }}>
                    {Number(totalTax).toLocaleString()} ₫
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={7} align="right">
                  <Text strong>Phí vận chuyển:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ fontSize: "16px" }}>
                    {Number(order.shippingCharge).toLocaleString()} ₫
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={2} />
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={7} align="right">
                  <Text strong style={{ fontSize: "16px" }}>
                    TỔNG CỘNG:
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text
                    strong
                    style={{
                      color: "#1890ff",
                      fontSize: "18px",
                    }}
                  >
                    {Number(grandTotal).toLocaleString()} ₫
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={2} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
      {order.note && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>Ghi chú</span>
            </Space>
          }
          size="small"
          className="mb-4"
        >
          <Text style={{ whiteSpace: "pre-wrap" }}>{order.note}</Text>
        </Card>
      )}
      <Divider />
      {order && (
        <>
          <ModalEditPurchaseOrder
            open={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            purchaseOrder={order}
          />
          <ModalAddAwb
            open={isAddAwbModalOpen}
            onClose={() => setIsAddAwbModalOpen(false)}
            purchaseOrderId={order.id}
          />
          {awbData && (
            <ModalAwbDetail
              awbCode={awbData.awbCode}
              openDetailModal={isAwbDetailModalOpen}
              setOpenDetailModal={setIsAwbDetailModalOpen}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PurchaseOrderDetail;
